import { differenceInCalendarDays } from 'date-fns';
import type { TFunction } from 'i18next';
import type { CalendarEvent } from '@/features/calendar/public';
import { parseDatePickerValue } from '@/shared/lib/datetime/instant';
import type { TimeOffRequestDto, TimeOffRequestStatus } from '../types';

/** Only these statuses occupy a calendar event — rejected/cancelled requests only appear in "Mis solicitudes". */
const VISIBLE_STATUSES: TimeOffRequestStatus[] = ['Approved', 'Pending'];

/**
 * Splits a request's active `YYYY-MM-DD` days into contiguous runs. A request
 * can have a day cancelled mid-range (`RequestDayStatus`), which must not be
 * bridged into a single spanning event — each run becomes its own event.
 */
function contiguousRuns(dates: string[]): string[][] {
  const sorted = [...dates].sort();
  const runs: string[][] = [];
  for (const date of sorted) {
    const current = runs[runs.length - 1];
    const previous = current?.[current.length - 1];
    const gap = previous
      ? differenceInCalendarDays(parseDatePickerValue(date), parseDatePickerValue(previous))
      : null;
    if (gap === 1) {
      current.push(date);
    } else {
      runs.push([date]);
    }
  }
  return runs;
}

/**
 * Maps a member's time-off requests to `CalendarEvent[]` for the shared
 * `CalendarView` (`source: 'khronox'`, colored by `metadata.status` — see
 * `CalendarEventCard`). Mirrors the label rule from the previous day-grid:
 * an approved request shows its type name, a pending one shows the status.
 */
export function timeOffRequestsToCalendarEvents(
  requests: TimeOffRequestDto[],
  typeNameById: Map<string, string>,
  t: TFunction,
): CalendarEvent[] {
  const events: CalendarEvent[] = [];

  for (const request of requests) {
    if (!request.id || !request.status || !VISIBLE_STATUSES.includes(request.status)) continue;

    const activeDates = (request.days ?? [])
      .filter((day) => day.status !== 'Cancelled')
      .map((day) => day.date.slice(0, 10));
    if (activeDates.length === 0) continue;

    const title =
      request.status === 'Approved'
        ? (typeNameById.get(request.typeId ?? '') ?? '')
        : t(`requests.status.${request.status}`);

    contiguousRuns(activeDates).forEach((run, index) => {
      events.push({
        id: `${request.id}-${index}`,
        title,
        start: parseDatePickerValue(run[0]),
        end: parseDatePickerValue(run[run.length - 1]),
        allDay: true,
        type: 'native',
        source: 'khronox',
        metadata: { requestId: request.id, status: request.status },
      });
    });
  }

  return events;
}
